import './index.css'
import { createApp } from 'vue'
import Toast from './component'
import Loading from '~~/components/basic/Loading.vue'
import { MASK_TYPE, TOAST_TYPE, IToastOptions } from './component'

const mountCom = (component: Component, options?: Record<string, unknown>) => {
  const wrap = document.createElement('div')
  wrap.id = 'toast_singleton'
  const app = createApp(component, options)
  const instance = app.mount(wrap)
  document.body.appendChild(wrap)
  return {
    instance,
    unmounted() {
      app.unmount()
      document.body.removeChild(wrap)
      window.singleton = null
    },
  }
}

const getCurrentInstance = () => {
  if (!window.singleton) {
    window.singleton = mountCom(Toast)
    return window.singleton
  }
  return window.singleton
}

function createToast(options?: IToastOptions | string) {
  if (!window) return
  const { instance } = getCurrentInstance()
  instance.show(options)
  return instance
}

function destroyToast() {
  const { instance, unmounted } = getCurrentInstance()
  // toast类型为message时，强制toast三秒自行隐藏后再销毁组件实例
  if (instance.options?.type === TOAST_TYPE.MESSAGE) {
    watch(
      () => instance.state,
      (newVal) => !newVal && unmounted(),
    )
  } else {
    instance.hide()
    unmounted()
  }
}

/**
 * toast loading
 * @message Loading 文案
 * @white 白底或黑底 Loading，默认 false
 * @mask 遮罩背景颜色, 默认透明
 */
export function showLoading(message?: string, white?: boolean, mask = MASK_TYPE.TRANSPARENT) {
  const { $i18n } = useNuxtApp()
  const options: IToastOptions = {
    mask,
    type: TOAST_TYPE.CUSTOM,
    duration: 0,
    render: () => h(Loading, { white: !white }),
    message: message || $i18n.t('model_loading') || '',
  }
  white && (options.white = white)
  return createToast(options)
}

/**
 * 隐藏 toast loading
 */
export const hideLoading = () => destroyToast()

/**
 * 全页面 loading
 * @param message loading 文案
 */
export const showPageLoading = (message?: string) => showLoading(message, true, MASK_TYPE.WHITE)

/**
 * 隐藏全页面 loading
 */
export const hidePageLoading = () => destroyToast()

/**
 * 纯文字 toast
 * @param message toast 文案
 */
export const showMessage = (message?: string) => createToast({ message, type: TOAST_TYPE.MESSAGE })

/**
 * 隐藏 纯文字 toast
 */
export const hideMessage = () => destroyToast()

enum _ICON_TYPE {
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
  WARN = 'WARN',
}

const icons = {
  [_ICON_TYPE.SUCCESS]: 'toast_success',
  [_ICON_TYPE.FAIL]: 'toast_fail',
  [_ICON_TYPE.WARN]: 'toast_warn',
}

function showIconToast(type: _ICON_TYPE, message: string) {
  return createToast({
    type: TOAST_TYPE.ICON,
    iconName: icons[type],
    message: message,
  })
}

/**
 * 带 success 图标的 toast
 */
export const showSuccessToast = (message: string) => showIconToast(_ICON_TYPE.SUCCESS, message)

/**
 * 带 fail 图标的 toast
 */
export const showFailToast = (message: string) => showIconToast(_ICON_TYPE.FAIL, message)

/**
 * 带 warn 图标的 toast
 */
export const showWarnToast = (message: string) => showIconToast(_ICON_TYPE.WARN, message)

export { MASK_TYPE, TOAST_TYPE, createToast, destroyToast as hideToast }
